import {useEffect, useRef, useState} from 'react';
import {
    createBotMessagePayload,
    createSelfMessagePayload,
} from './utils/message-utils';
import ConvMessageContainer from './components/messenger-container';

import Amplify from '@aws-amplify/core'
import * as realTimeApi from './real-time-api-config'

import {Switch} from '@headlessui/react'
import {HiOutlinePaperAirplane, HiOutlineDotsHorizontal} from 'react-icons/hi'
import {getMessages, setHandleBy} from "../../../services/session";
import {getSessionTimeline} from "../../../services/model/trace-service";
import {useAppContext} from "../../../helpers/hooks/common-hook";
import {useCanRevealPii} from "../../../helpers/hooks/pii-reveal-hook";
import {createUniqueId} from "../../../helpers/utils/object-util";
import Loader from './components/loader';

Amplify.configure(realTimeApi.config)

const HANDLE_BY_HUMAN = 'human'
const HANDLE_BY_AI = 'ai'
const SENT_BY_BOT = 'bot'
const SENT_BY_USER = 'user'
const CHANNEL_WEB = 'web'

// Spans are exported as the turn ends and ingested asynchronously, so a refresh
// fired the instant a reply lands would usually read the trace back empty.
const TRACE_REFRESH_DELAY_MS = 1500

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

function isEnabledAIHandle(session) {
    return session.handleBy !== HANDLE_BY_HUMAN
}

function Page({appId: envAppId, session: sessionProp, accessKey, chatBotSetting }) {
    const { appInfo } = useAppContext();
    // The environment's id (`<appId>` or `<appId>_live`): messages, traces,
    // real-time channels and takeovers all live under it.
    const appId = envAppId || appInfo.appId;

    const [session, setSession] = useState({sessionId: null, refId: null});
    const [question, setQuestion] = useState();
    const [botReply, setBotReply] = useState();
    const [userReply, setUserReply] = useState();

    const [messages, setMessages] = useState([]);

    // Personal data is masked. A security admin can switch to the stored values; it starts
    // off for every conversation, and the API checks the role on each request.
    const canReveal = useCanRevealPii();
    const [reveal, setReveal] = useState(false);
    const revealRef = useRef(false);

    const [formState, setFormState] = useState({
        processing: false,
        success: false,
        failed: false,
        errorMessage: '',
    });

    const [error, setError] = useState({
        question: false,
    });

    // messageId -> trace group for that turn, as returned by the tracing API.
    const [traceGroups, setTraceGroups] = useState({});
    const [tracesLoading, setTracesLoading] = useState(false);
    // Separates "still fetching for the first time" from "fetched, and this
    // message simply has no trace" - the two look identical from a bubble.
    const [tracesLoaded, setTracesLoaded] = useState(false);
    // True when the API answered without honouring groupBy - almost always a
    // trace-api that has not been redeployed yet.
    const [tracesUnsupported, setTracesUnsupported] = useState(false);

    const toMessages = response => {
        const _messages = []
        for (const resp of response.data) {
            if (resp.sentBy === 'bot') {
                _messages.push(createBotMessagePayload(resp.message, resp.createdAt, resp.messageId))
            } else {
                _messages.push(createSelfMessagePayload(resp.message, resp.createdAt, resp.messageId))
            }
        }
        return _messages
    }

    const fetchMessages = async () => {
        if (session.sessionId) {
            setMessages([])
            updateFetchMessagesState(true, false)
            const response = await getMessages(appId, session.sessionId, revealRef.current);
            setMessages(toMessages(response))
            updateFetchMessagesState(false, true)
        }
    }

    // Reload what is on screen with the other setting. The ref is set first because the
    // fetchers read it; the state only drives the checkbox.
    const handleToggleReveal = checked => {
        revealRef.current = checked
        setReveal(checked)
        fetchMessages()
        fetchTraceGroups()
    }

    /**
     * Load the session's spans bucketed per triggering message.
     *
     * Fetched alongside the transcript rather than per message: one request
     * covers the whole session, where a request per bubble would fan out into
     * dozens. Tracing is supplementary, so a failure resolves to an empty map
     * and the transcript renders unchanged.
     */
    const fetchTraceGroups = async () => {
        if (!session.sessionId || !appId) {
            return;
        }

        setTracesLoading(true);
        const response = await getSessionTimeline(appId, session.sessionId, revealRef.current);

        // The API ignored groupBy, so there is nothing to attribute. Surfaced in
        // the header rather than left as an empty transcript, which is
        // indistinguishable from a session that genuinely has no traces.
        setTracesUnsupported(Boolean(response.unsupported));

        const byMessageId = {};
        (response.data || []).forEach(group => {
            // Groups with no messageId predate the message stamping and cannot
            // be attributed to a bubble, so they are dropped from this view.
            if (group.messageId) {
                byMessageId[group.messageId] = group;
            }
        });

        setTraceGroups(byMessageId);
        setTracesLoading(false);
        setTracesLoaded(true);
    }

    useEffect( () => {
        if (sessionProp) {
            setSession(sessionProp);
        }
    }, [sessionProp]);

    useEffect(() => {
        let subscriptionBotMessage;
        let subscriptionUserMessage;

        // Revealed values belong to the conversation they were asked for.
        revealRef.current = false
        setReveal(false)

        if (session.sessionId) {
            fetchMessages();
            setTraceGroups({});
            setTracesLoaded(false);
            setTracesUnsupported(false);
            fetchTraceGroups();
            subscriptionBotMessage = realTimeApi.subscribeBotMessage(accessKey, appId, session.sessionId, ({data}) => {
                data = JSON.parse(data);
                setBotReply(data);
            })

            subscriptionUserMessage = realTimeApi.subscribeUserMessage(accessKey, appId, session.sessionId, ({data}) => {
                data = JSON.parse(data);
                setUserReply(data);
            })
        }
        return () => {
            if (subscriptionBotMessage) {
                subscriptionBotMessage.unsubscribe()
            }

            if (subscriptionUserMessage) {
                subscriptionUserMessage.unsubscribe()
            }
        }
    }, [session.sessionId])

    useEffect(() => {
        if (botReply) {
            appendMessage(createBotMessagePayload(botReply.message, undefined, botReply.messageId));
        }
    }, [botReply]);

    /**
     * A bot reply means the turn finished, so its spans are the ones worth
     * fetching. The runtime flushes them as the turn ends and ingest is
     * asynchronous, so this waits briefly rather than racing the exporter -
     * and it is only a refresh, so losing the race merely delays the steps
     * until the next turn or a reopen.
     */
    useEffect(() => {
        if (!botReply || !session.sessionId) {
            return;
        }

        const timer = setTimeout(() => {
            fetchTraceGroups();
        }, TRACE_REFRESH_DELAY_MS);

        return () => clearTimeout(timer);
    }, [botReply]);

    useEffect(() => {
        if (userReply) {
            appendMessage(createSelfMessagePayload(userReply.message, undefined, userReply.messageId));
        }
    }, [userReply]);

    const appendMessage = (message) => {
        const newMessages = Object.assign([], messages);
        newMessages.push(message);
        setMessages(newMessages);
    };

    const validateForm = () => {
        let valid = true;
        if (!question) {
            error.question = true;
            valid = false;
        } else {
            error.question = false;
        }

        setError(Object.assign({}, error));
        return valid;
    };

    const updateFormState = (processing, success, failed, errorMessage) => {
        setFormState(
            Object.assign({}, {processing, success, failed, errorMessage})
        );
    };

    const onChangeQuestion = (event) => {
        setQuestion(event.target.value);
    };

    const handleOnAskSubmit = async (event) => {
        event.preventDefault();
        if (!formState.processing && validateForm()) {
            updateFormState(true, false, false);

            const dataRequest = {
                appId: appId,
                sessionId: session.sessionId,
                messageId: createUniqueId(),
                type: "FULFILLMENT",
                message: {
                    type: 'TEXT',
                    content: question,
                },
                channel: CHANNEL_WEB,
                handleBy: HANDLE_BY_HUMAN,
                sentBy: SENT_BY_BOT,
            };

            await realTimeApi.sendBotMessage(accessKey, appId, session.sessionId, JSON.stringify(dataRequest))

            updateFormState(false, true, false);
            setQuestion('');
        }
    };

    const handleOnChangeHandleBy = async checked => {
        const handleBy = checked? HANDLE_BY_AI: HANDLE_BY_HUMAN
        updateChangeAIHandleState(true)
        await setHandleBy(appId, session.sessionId, handleBy)
        updateChangeAIHandleState(false, true)
        setSession({...session, handleBy})
    }

    const [fetchMessagesState, setFetchMessagesState] = useState({
        processing: false,
        success: false,
        failed: false,
        errorMessage: ''
    });

    const updateFetchMessagesState = (processing, success, failed, errorMessage) => {
        setFetchMessagesState(Object.assign({}, { processing, success, failed, errorMessage }))
    }

    const [changeAIHandleState, setChangeAIHandleState] = useState({
        processing: false,
        success: false,
        failed: false,
        errorMessage: ''
    });

    const updateChangeAIHandleState = (processing, success, failed, errorMessage) => {
        setChangeAIHandleState(Object.assign({}, { processing, success, failed, errorMessage }))
    }

    const aiHandling = isEnabledAIHandle(session);

    return (
        <div className='flex h-full min-h-0 min-w-0 flex-col bg-white'>

            {/* Header */}
            <div className='flex h-12 shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4'>
                <div className='min-w-0'>
                    <h2 className='truncate text-sm font-semibold text-gray-900'>
                        Session {session.refId}
                    </h2>
                    {tracesUnsupported &&
                        <p
                            className='truncate text-[10px] text-amber-600'
                            title='The trace API answered without grouping spans by message. Deploy the tracing backend and runtime-handler to enable per-message steps.'
                        >
                            Steps unavailable &mdash; tracing API not deployed
                        </p>
                    }
                </div>

                <div className='flex shrink-0 items-center gap-2'>
                    {canReveal &&
                        <label
                            className='flex items-center gap-1.5 text-xs font-medium text-gray-500'
                            title='Personal data is masked. Show the stored values (security admins only).'
                        >
                            <input type='checkbox' checked={reveal} onChange={event => handleToggleReveal(event.target.checked)}/>
                            Show raw value
                        </label>
                    }
                    {/* Who is answering, stated in words - a bare switch left it
                        ambiguous which way "on" meant. */}
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${aiHandling
                        ? 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'
                        : 'bg-amber-50 text-amber-700 ring-amber-600/20'
                        }`}>
                        {aiHandling ? 'Bot is replying' : 'You are replying'}
                    </span>

                    {changeAIHandleState.processing?
                        <Loader/>
                        :
                        <Switch.Group as="div" className="flex items-center gap-1.5">
                            <Switch.Label as="span">
                                <span className="text-xs font-medium text-gray-500">AI Bot</span>
                            </Switch.Label>
                            <Switch
                                checked={aiHandling}
                                onChange={handleOnChangeHandleBy}
                                className={classNames(
                                    aiHandling ? 'bg-indigo-600' : 'bg-gray-200',
                                    'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                                )}
                            >
                            <span
                                aria-hidden="true"
                                className={classNames(
                                    aiHandling ? 'translate-x-4' : 'translate-x-0',
                                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                )}
                            />
                            </Switch>
                        </Switch.Group>
                    }
                </div>
            </div>

            {/* Transcript. Owns the scroll so the header and composer stay put. */}
            <div className='min-h-0 flex-1 overflow-y-auto bg-gray-50 py-3'>
                {fetchMessagesState.processing && messages.length === 0 &&
                    <div className='flex h-full items-center justify-center'>
                        <Loader/>
                    </div>
                }

                {messages.map((message, mi) => {
                    const traceGroup = message.sourceMessageId
                        ? traceGroups[message.sourceMessageId]
                        : undefined;
                    return (
                        <ConvMessageContainer
                            key={mi}
                            {...message}
                            traceGroup={traceGroup}
                            // Only the very first load shows a placeholder.
                            // Later refreshes keep the steps already on
                            // screen rather than flickering, and messages
                            // that never had a trace (human-handled turns)
                            // stay clean instead of loading forever.
                            traceLoading={tracesLoading && !tracesLoaded}
                        />
                    );
                })}

                {!fetchMessagesState.processing && messages.length === 0 &&
                    <div className='flex h-full items-center justify-center'>
                        <p className='text-xs text-gray-500'>No messages in this session yet.</p>
                    </div>
                }
            </div>

            {/* Composer */}
            <form
                className='shrink-0 border-t border-gray-200 p-2.5'
                onSubmit={handleOnAskSubmit}
            >
                <div className={`flex items-end gap-2 rounded-lg border px-2.5 py-1.5 transition-colors ${error.question ? 'border-red-300' : 'border-gray-200 focus-within:border-indigo-500'
                    }`}>
                    <textarea
                        rows={1}
                        name='inputText'
                        className='max-h-28 min-h-[1.5rem] w-full resize-none border-0 p-0 text-sm outline-none placeholder:text-gray-400 focus:ring-0'
                        placeholder={aiHandling ? 'Send a message as the bot...' : 'Reply to the customer...'}
                        value={question || ''}
                        onChange={onChangeQuestion}
                        // Enter sends, Shift+Enter breaks the line - the usual
                        // chat contract, and the reason this is a form submit.
                        onKeyDown={event => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                handleOnAskSubmit(event);
                            }
                        }}
                        maxLength='90'
                    />

                    <button
                        type='submit'
                        disabled={formState.processing || !question}
                        className='flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-indigo-600 text-fixed-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400'
                        title='Send message'
                    >
                        {formState.processing
                            ? <HiOutlineDotsHorizontal size={15} className='animate-pulse' />
                            : <HiOutlinePaperAirplane size={15} className='rotate-90' />}
                    </button>
                </div>

                <div className='flex items-center justify-between px-1 pt-1'>
                    <span className='text-[10px] text-gray-400'>
                        Enter to send &middot; Shift+Enter for a new line
                    </span>
                    {/* The 90 character cap is enforced by the input; showing it
                        only near the limit keeps it from reading as clutter. */}
                    {(question || '').length > 60 &&
                        <span className='text-[10px] tabular-nums text-gray-400'>
                            {(question || '').length}/90
                        </span>
                    }
                </div>
            </form>
        </div>
    );
}

export default Page;
